// R116 #5 (Jorge: "It takes too long to upload pics one by one… some issues
// are impossible to show in a picture") — the multi-file evidence picker that
// sits INSIDE a form (the work-order Tech report, first): photos AND video,
// several at once, through the background queue (lib/photoUploader — persisted,
// photos 3-wide / clips 1-wide, client_key, the server's size cap).
//
//   • one blue drop zone: tap → the picker (camera roll multi-select on a phone);
//     on a desktop, drag files onto it or paste a screenshot (⌘/Ctrl+V while
//     focus is anywhere inside the form — Jorge relays vendor screenshots);
//   • a busy state while files are prepared (the input is disabled — a second
//     pick of the same files would mint new keys the server can't dedupe);
//   • the files already on the record + one tile per queued file with its own
//     progress bar, live (no redraw: 'surge-photo-progress' patches the bar);
//   • never blocks the form's own save — the report can be filed while the
//     files are still uploading (the message says so).
//
// Pages render evidencePickerHtml(cfg) and call bindEvidencePicker(host, cfg)
// after each render (idempotent per element). cfg.onUploaded hands back the
// record's new media when a file lands (the page patches its own copy).
import {
  enqueuePhotos, pendingItems, uploadCapBytes, loadUploadCap,
} from '../../lib/photoUploader';
import {
  mediaStrip, bindMediaStrips, pendingTilesHtml, patchPendingProgress, releasePendingPreviews,
  isMediaFile, EVIDENCE_ACCEPT, pickSummary, fmtBytes, ensureMediaCss, type MediaItem,
} from '../lib/media';

export interface EvidencePickerCfg {
  /** Unique per picker on the page, e.g. `wo-after-<woId>`. */
  id: string;
  /** Section label, e.g. "After evidence". */
  title: string;
  /** Upload endpoint (one file per request), e.g. /api/assetops/work-orders/<id>/photo. */
  url: string;
  /** Form fields sent with every file (kind, by, …) — the queue adds client_key. */
  fields: Record<string, string>;
  /** Which queued files belong to this picker (same url + e.g. kind === 'after'). */
  match: (fields: Record<string, string>) => boolean;
  /** Media already on the record. */
  existing: MediaItem[];
  /** Queue label ("after photo") — shows in the app-wide chip's error notes. */
  label?: string;
  /** Muted helper line under the title. */
  hint?: string;
  /** Called when a file of this picker lands; return the record's media now (else the strip keeps `existing`). */
  onUploaded?: (response: any) => MediaItem[] | void;
}

const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const urlPath = (u: string) => { try { return new URL(u, location.href).pathname; } catch { return u; } };

interface Live { host: HTMLElement; cfg: EvidencePickerCfg; existing: MediaItem[]; busy: boolean }
const live = new Map<string, Live>();
const prune = () => { for (const [k, v] of live) if (!v.host.isConnected) live.delete(k); };

function mediaHtml(l: Live): string {
  const pend = pendingItems(urlPath(l.cfg.url), l.cfg.match);
  const strip = mediaStrip(l.existing, { size: 'md', alt: l.cfg.title.toLowerCase(), append: pendingTilesHtml(pend) });
  return strip || `<div class="evp-empty">Nothing yet — add photos or a short video.</div>`;
}
function countsHtml(l: Live): { n: number; up: number } {
  const up = pendingItems(urlPath(l.cfg.url), l.cfg.match).length;
  return { n: l.existing.length + up, up };
}

/** The picker's markup (a <section>; the page's own form wraps it). */
export function evidencePickerHtml(cfg: EvidencePickerCfg): string {
  ensureMediaCss(); ensureEvpCss();
  const l: Live = live.get(cfg.id) || { host: null as any, cfg, existing: cfg.existing || [], busy: false };
  l.cfg = cfg; l.existing = cfg.existing || [];
  const c = countsHtml(l);
  return `<section class="evp" data-evp="${esc(cfg.id)}" aria-label="${esc(cfg.title)}">
    <div class="evp-h"><span class="evp-t">${esc(cfg.title)}</span><span class="evp-n" data-evp-n>${c.n}</span><span class="evp-up" data-evp-up${c.up ? '' : ' hidden'}>${c.up ? `${c.up} uploading` : ''}</span></div>
    ${cfg.hint ? `<div class="evp-hint">${esc(cfg.hint)}</div>` : ''}
    <div class="evp-media" data-evp-media>${mediaHtml(l)}</div>
    <label class="evp-drop" data-evp-drop>
      <input type="file" accept="${EVIDENCE_ACCEPT}" multiple class="evp-file" data-evp-file>
      <span class="evp-cta" data-evp-cta>📷 Add photos or video</span>
      <span class="evp-sub">Pick several at once<span class="evp-desk"> · drop files here · paste a screenshot</span></span>
    </label>
    <div class="evp-msg" data-evp-msg role="status" aria-live="polite"></div>
  </section>`;
}

function redraw(l: Live): void {
  const root = l.host.querySelector<HTMLElement>('[data-evp-media]');
  if (root) root.innerHTML = mediaHtml(l);
  const c = countsHtml(l);
  const n = l.host.querySelector<HTMLElement>('[data-evp-n]');
  const up = l.host.querySelector<HTMLElement>('[data-evp-up]');
  if (n) n.textContent = String(c.n);
  if (up) { up.hidden = !c.up; up.textContent = c.up ? `${c.up} uploading` : ''; }
}
function setBusy(l: Live, text: string | null): void {
  l.busy = !!text;
  const inp = l.host.querySelector<HTMLInputElement>('[data-evp-file]');
  const cta = l.host.querySelector<HTMLElement>('[data-evp-cta]');
  const drop = l.host.querySelector<HTMLElement>('[data-evp-drop]');
  if (inp) inp.disabled = !!text;
  if (cta) cta.textContent = text || '📷 Add photos or video';
  if (drop) { drop.classList.toggle('evp-busy', !!text); drop.setAttribute('aria-busy', text ? 'true' : 'false'); }
}
function say(l: Live, text: string, tone: '' | 'ok' | 'warn' = ''): void {
  const m = l.host.querySelector<HTMLElement>('[data-evp-msg]');
  if (!m) return;
  m.textContent = text;
  m.className = `evp-msg${tone ? ` ${tone}` : ''}`;
}

/** Queue files for one picker (the pick / drop / paste paths all land here). */
async function take(l: Live, files: File[], how: string): Promise<void> {
  if (l.busy) return;   // double-pick guard
  const media = files.filter(isMediaFile);
  const skipped = files.length - media.length;
  if (!media.length) { say(l, skipped ? 'Only photos and videos can be added here.' : '', skipped ? 'warn' : ''); return; }
  setBusy(l, `Preparing ${media.length} file${media.length === 1 ? '' : 's'}…`);   // synchronously — the guard holds from the first tick
  say(l, '');
  let n = 0, cap = uploadCapBytes();
  try {
    cap = await loadUploadCap();
    n = await enqueuePhotos(l.cfg.url, media, { ...l.cfg.fields }, l.cfg.label || 'evidence file');
  } catch {
    n = 0;
  } finally {
    setBusy(l, null);
  }
  const big = media.filter(f => f.size > cap);
  redraw(l);
  const bits: string[] = [];
  if (n) bits.push(`${pickSummary(media.filter(f => f.size <= cap))} ${how} — uploading in the background. You can keep going; nothing waits for it.`);
  if (big.length) bits.push(`"${big[0].name}" is ${fmtBytes(big[0].size)} — the limit is ${fmtBytes(cap)}. Record a shorter clip (about 2 min).`);
  if (skipped) bits.push(`${skipped} file${skipped === 1 ? ' was' : 's were'} not a photo or video.`);
  if (!n && !big.length && !skipped) bits.push("Those files couldn't be queued — try again.");
  say(l, bits.join(' '), big.length || skipped || !n ? 'warn' : 'ok');
}

/** Wire one rendered picker (call after every render of its host). */
export function bindEvidencePicker(scope: ParentNode, cfg: EvidencePickerCfg): void {
  installGlobal();
  const host = scope.querySelector<HTMLElement>(`[data-evp="${CSS.escape(cfg.id)}"]`);
  if (!host || (host as any)._evp) return;
  (host as any)._evp = true;
  const prev = live.get(cfg.id);
  const l: Live = { host, cfg, existing: cfg.existing || [], busy: false };
  if (prev && prev.busy && prev.host.isConnected === false) l.busy = false;
  live.set(cfg.id, l);
  prune();
  bindMediaStrips(host);
  const inp = host.querySelector<HTMLInputElement>('[data-evp-file]');
  inp?.addEventListener('change', () => {
    const files = Array.from(inp.files || []);
    inp.value = '';   // the same file can be picked again later — the busy guard stops a double-pick now
    if (files.length) void take(l, files, 'added');
  });
  const drop = host.querySelector<HTMLElement>('[data-evp-drop]');
  const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
  host.addEventListener('dragenter', (e) => { if (!hasFiles(e)) return; e.preventDefault(); drop?.classList.add('evp-over'); });
  host.addEventListener('dragover', (e) => { if (!hasFiles(e)) return; e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = l.busy ? 'none' : 'copy'; drop?.classList.add('evp-over'); });
  host.addEventListener('dragleave', (e) => { if (e.target === host || !host.contains(e.relatedTarget as Node)) drop?.classList.remove('evp-over'); });
  host.addEventListener('drop', (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    drop?.classList.remove('evp-over');
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) void take(l, files, 'dropped in');
  });
}

// ── one set of window listeners for every picker ───────────────────────────
let installed = false;
function installGlobal(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const forUrl = (detail: any): Live[] => { prune(); return [...live.values()].filter(l => detail && urlPath(String(detail.url || '')) === urlPath(l.cfg.url) && l.cfg.match(detail.fields || {})); };
  window.addEventListener('surge-photo-queue', () => {
    prune();
    for (const l of live.values()) redraw(l);
    const keys = new Set<string>();
    for (const l of live.values()) for (const p of pendingItems(urlPath(l.cfg.url), l.cfg.match)) keys.add(p.key);
    releasePendingPreviews(keys);
  });
  window.addEventListener('surge-photo-progress', (e: Event) => {
    const d = (e as CustomEvent).detail || {};
    for (const l of forUrl(d)) patchPendingProgress(l.host, String(d.clientKey || ''), Number(d.sent) || 0, Number(d.total) || 0);
  });
  window.addEventListener('surge-photo-uploaded', (e: Event) => {
    const d = (e as CustomEvent).detail || {};
    for (const l of forUrl(d)) {
      let next: MediaItem[] | void = undefined;
      try { next = l.cfg.onUploaded ? l.cfg.onUploaded(d.response) : undefined; } catch { /* the page's bookkeeping only */ }
      if (Array.isArray(next)) l.existing = next;
      redraw(l);
    }
  });
  window.addEventListener('surge-photo-dropped', (e: Event) => {
    const d = (e as CustomEvent).detail || {};
    for (const l of forUrl(d)) { say(l, `⚠ ${d.note || 'A file could not be uploaded.'}`, 'warn'); redraw(l); }
  });
  // Paste a screenshot (⌘/Ctrl+V): the picker whose form holds the focus, else
  // the only picker on screen. Text pastes are never touched.
  document.addEventListener('paste', (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files || []).filter(isMediaFile);
    if (!files.length) return;
    prune();
    const pickers = [...live.values()].filter(l => l.host.offsetParent !== null || l.host.getClientRects().length);
    if (!pickers.length) return;
    const act = document.activeElement as HTMLElement | null;
    const scopeOf = (l: Live) => (l.host.closest('[data-evp-scope]') as HTMLElement | null) || l.host;
    let target = act ? pickers.find(l => scopeOf(l).contains(act)) : undefined;
    if (!target) {
      const editable = act && (act.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(act.tagName));
      if (editable || pickers.length !== 1) return;   // focus is in some other field, or it's ambiguous
      target = pickers[0];
    }
    e.preventDefault();
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const named = files.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `screenshot-${stamp}${files.length > 1 ? `-${i + 1}` : ''}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type || 'image/png' })));
    void take(target, named, 'pasted');
  });
}

const EVP_CSS_ID = 'evp-css';
const EVP_CSS = `
.evp { display:flex; flex-direction:column; gap:8px; margin-top:12px; }
.evp-h { display:flex; align-items:center; gap:6px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
.evp-n { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px; padding:0 6px; border-radius:999px; background:var(--accent-soft); color:var(--accent); font-size:10.5px; letter-spacing:0; font-variant-numeric:tabular-nums; }
.evp-up { text-transform:none; letter-spacing:0; font-weight:600; color:var(--accent); }
.evp-up[hidden] { display:none; }
.evp-hint { font-size:12px; color:var(--text-faint); line-height:1.45; }
.evp-empty { font-size:12px; color:var(--text-faint); }
.evp-drop { position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2px; min-height:56px; padding:10px 14px; box-sizing:border-box; border:1.5px dashed var(--accent-border); border-radius:var(--radius-sm, 8px); background:var(--accent-soft); color:var(--accent); cursor:pointer; text-align:center; transition:background .15s ease, border-color .15s ease; }
.evp-drop:hover { border-color:var(--accent); }
.evp-drop:focus-within { outline:2px solid var(--accent); outline-offset:2px; }
.evp-drop.evp-over { border-style:solid; border-color:var(--accent); background:var(--card); }
.evp-drop.evp-busy { cursor:progress; opacity:.8; }
.evp-file { position:absolute; width:1px; height:1px; opacity:0; pointer-events:none; }
.evp-cta { font-weight:700; font-size:13.5px; }
.evp-sub { font-size:11.5px; color:var(--text-muted); font-weight:500; }
.evp-desk { display:none; }
@media (hover:hover) and (pointer:fine) { .evp-desk { display:inline; } }
.evp-msg { font-size:12px; color:var(--text-muted); line-height:1.45; }
.evp-msg:empty { display:none; }
.evp-msg.ok { color:var(--success-text); }
.evp-msg.warn { color:var(--warning-text); }
`;
function ensureEvpCss(): void {
  if (typeof document === 'undefined' || document.getElementById(EVP_CSS_ID)) return;
  const st = document.createElement('style');
  st.id = EVP_CSS_ID;
  st.textContent = EVP_CSS;
  document.head.appendChild(st);
}
